import type { AnalyticsGrain, AnalyticsOverviewDto } from '@pams/shared';
import { FigureList } from '@/components/DataFigures';
import { formatCount, seriesPeak, seriesPointLabel, type SeriesPoint } from '@/lib/analyticsReport';
import { USAGE_ANALYTICS_COPY } from '../copy';
import { SubBlock } from './AnalyticsSection';
import { SeriesBars } from './SeriesBars';

/** Adds two estimates; unknown if either half is. */
const bothOrNull = (a: number | null, b: number | null): number | null => (a === null || b === null ? null : a + b);

/**
 * One headline figure.
 *
 * ⚠️ NOT `BigNumber`, WHICH STAYED WITH THE DASHBOARD'S TILES. That one carries a `StatusDot`
 * because a tile's number is a STATUS — red when nobody is named. A request count is not good or
 * bad, and a dot beside it would spend the colour that means "look at this" on a quantity.
 */
const Headline = ({ label, value, caption }: { label: string; value: string; caption?: string }) => (
  <div className="flex min-w-0 flex-col gap-1" data-figure={label}>
    <span className="text-xs text-muted-foreground">{label}</span>
    <span className="text-3xl font-semibold tabular-nums text-foreground">{value}</span>
    {caption ? <span className="text-xs text-muted-foreground">{caption}</span> : null}
  </div>
);

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE WINDOW AT A GLANCE. [ANALYTICS §5]
 *
 *  ⚠️ `≈` ON EVERY UNIQUE COUNT. Unique officers and sessions are HyperLogLog estimates on the
 *  server, and a bare integer would state a precision nobody measured. `null` is a THIRD answer:
 *  the counter store did not answer, so the figure is a dash and a sentence says why — never a
 *  zero, which would read as nobody signed in.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const OverviewSection = ({ data, grain }: { data: AnalyticsOverviewDto; grain: AnalyticsGrain }) => {
  const copy = USAGE_ANALYTICS_COPY.overview;
  const sessions = bothOrNull(data.sessions.dashboard, data.sessions.officer);
  const uniquesMissing = data.uniqueOfficers.total === null || sessions === null;
  const points: SeriesPoint[] = data.series.map((point) => ({ at: point.at, values: [point.requests] }));
  const peak = seriesPeak(points);
  const anonymous = copy.anonymous(data.requestsBySurface.none);

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-4 @min-[720px]:grid-cols-4">
        <Headline label={copy.requests} value={formatCount(data.requests)} />
        <Headline
          label={copy.uniqueOfficers}
          value={copy.estimate(data.uniqueOfficers.total)}
          caption={copy.estimateCaption}
        />
        <Headline label={copy.sessions} value={copy.estimate(sessions)} caption={copy.estimateCaption} />
        <Headline
          label={copy.signIns}
          value={formatCount(data.signIns.dashboard + data.signIns.officer)}
          caption={copy.countedSinceRelease}
        />
      </div>
      {uniquesMissing ? <p className="text-sm text-muted-foreground">{copy.uniquesUnavailable}</p> : null}

      <div className="grid gap-6 @min-[880px]:grid-cols-2">
        <SubBlock title={copy.surfaceHeading} caption={copy.surfaceCaption}>
          <table className="w-full text-left text-sm tabular-nums" data-testid="overview-surfaces">
            <thead>
              <tr className="text-xs text-muted-foreground">
                <th scope="col" className="py-1 pr-3 font-medium">
                  {copy.surfaceColumn}
                </th>
                <th scope="col" className="py-1 pr-3 text-right font-medium">
                  {copy.dashboard}
                </th>
                <th scope="col" className="py-1 text-right font-medium">
                  {copy.officerApp}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {[
                {
                  key: 'requests',
                  label: copy.requests,
                  dashboard: formatCount(data.requestsBySurface.dashboard),
                  officer: formatCount(data.requestsBySurface.officer),
                },
                {
                  key: 'officers',
                  label: copy.uniqueOfficers,
                  dashboard: copy.estimate(data.uniqueOfficers.dashboard),
                  officer: copy.estimate(data.uniqueOfficers.officer),
                },
                {
                  key: 'sessions',
                  label: copy.sessions,
                  dashboard: copy.estimate(data.sessions.dashboard),
                  officer: copy.estimate(data.sessions.officer),
                },
                {
                  key: 'signIns',
                  label: copy.signIns,
                  dashboard: formatCount(data.signIns.dashboard),
                  officer: formatCount(data.signIns.officer),
                },
              ].map((row) => (
                <tr key={row.key} data-row={row.key}>
                  <th scope="row" className="py-1.5 pr-3 font-normal text-muted-foreground">
                    {row.label}
                  </th>
                  <td className="py-1.5 pr-3 text-right text-foreground">{row.dashboard}</td>
                  <td className="py-1.5 text-right text-foreground">{row.officer}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {anonymous ? <p className="text-xs text-muted-foreground">{anonymous}</p> : null}
        </SubBlock>

        <SubBlock title={copy.roleHeading}>
          {data.requestsByRole.length === 0 ? (
            <p className="text-sm text-muted-foreground">{copy.noRoles}</p>
          ) : (
            <FigureList
              rows={data.requestsByRole.map((entry) => ({
                key: entry.role,
                label: copy.role(entry.role),
                value: formatCount(entry.count),
              }))}
            />
          )}
        </SubBlock>
      </div>

      <SubBlock title={copy.seriesTitle}>
        <SeriesBars
          testId="overview-series"
          series={[copy.seriesName]}
          points={points}
          grain={grain}
          summary={copy.seriesSummary(
            data.requests,
            peak === null ? null : { label: seriesPointLabel(peak.at, grain), total: peak.total },
            grain,
          )}
        />
      </SubBlock>
    </div>
  );
};
